import React from 'react';

type Props = {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
  className?: string;
};

/**
 * Tiny inline SVG sparkline. Accepts an array of numbers, draws a smooth area+line.
 * No external dep — the points are scaled to fit the viewBox.
 */
const Sparkline: React.FC<Props> = ({
  values,
  width = 100,
  height = 28,
  color = '#6366f1',
  className,
}) => {
  if (!values.length) {
    return <div style={{ width, height }} className={className} />;
  }

  const max = Math.max(...values, 1);
  const min = 0;
  const range = Math.max(max - min, 1);
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;

  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return [x, y] as const;
  });

  const linePath = points.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(' ');
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      preserveAspectRatio="none"
    >
      <path d={areaPath} fill={color} fillOpacity={0.12} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  );
};

export default Sparkline;
